import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/**
 * Resolves the annual ARR from a deal, handling both new and legacy data formats.
 * - New annual deals: image_revenue already stores the annual amount
 * - Legacy/upfront deals: image_revenue may store total contract value, needs dividing
 */
const getAnnualArrFromDeal = (deal: { image_revenue?: number | null; value?: number | null; deal_data?: any }) => {
  const dealValue = parseFloat(deal.value?.toString() || '0');
  if (dealValue <= 0) return 0;

  const dealData = deal.deal_data || {};
  const coverageYears = dealData.coverage_years || 1;

  // Primary: use raw annual_credits × price_per_image (most reliable across all deal versions)
  const annualCredits = dealData.annual_credits || dealData.annual_payment_breakdown?.annual_credits;
  const pricePerImage = dealData.price_per_image;

  if (annualCredits != null && pricePerImage != null) {
    return parseFloat(annualCredits.toString()) * parseFloat(pricePerImage.toString());
  }

  // Fallback: annualize image_revenue by dividing by coverage years
  if (deal.image_revenue) {
    return parseFloat(deal.image_revenue.toString()) / coverageYears;
  }

  return 0;
};

export const useKpiMetrics = () => {
  // Query for Actuals (Delivered/Completed)
  const actualsQuery = useQuery({
    queryKey: ['kpi-actuals'],
    queryFn: async () => {
      console.log("📊 Fetching actuals KPI metrics...");
      
      // Get delivered/shipped orders with sales data
      const { data: orders, error: ordersError } = await supabase
        .from('orders')
        .select(`
          id,
          total_cameras,
          total_images,
          status,
          sale_id,
          allocated_device_ids,
          created_at
        `)
        .in('status', ['delivered', 'shipped']);
      
      if (ordersError) {
        console.error("Error fetching orders:", ordersError);
        throw ordersError;
      }

      // Get sales data for revenue and ARR calculation
      const saleIds = orders?.filter(o => o.sale_id).map(o => o.sale_id) || [];
      let salesData: any[] = [];
      let salesWithDeals: any[] = [];
      
      if (saleIds.length > 0) {
        const { data: sales, error: salesError } = await supabase
          .from('sales')
          .select('id, total_amount, status, created_at')
          .in('id', saleIds);
        
        if (salesError) {
          console.error("Error fetching sales:", salesError);
        } else {
          salesData = sales || [];
        }

        // Get sales with deals for ARR calculation
        const { data: salesDeals, error: salesDealsError } = await supabase
          .from('sales')
          .select('id, deal_id, crm_deals(image_revenue, deal_data, value)')
          .in('id', saleIds);
        
        if (salesDealsError) {
          console.error("Error fetching sales with deals:", salesDealsError);
        } else {
          salesWithDeals = salesDeals || [];
        }
      }

      // Calculate metrics
      // Create a map of sale_id to deal value for commercial/trial split
      const saleValueMap = new Map(
        salesWithDeals?.map(s => [s.id, parseFloat(s.crm_deals?.value?.toString() || '0')]) || []
      );

      // Commercial: orders where deal value > 0
      const commercialOrders = orders?.filter(order => {
        if (!order.sale_id) return false;
        const dealValue = saleValueMap.get(order.sale_id) || 0;
        return dealValue > 0;
      }) || [];

      const camerasCommercial = commercialOrders.reduce((sum, order) => 
        sum + (order.total_cameras || 0), 0);

      // Trial: orders where deal value = 0 or no sale link
      const camerasTrial = orders?.reduce((sum, order) => {
        if (!order.sale_id) return sum + (order.total_cameras || 0);
        const dealValue = saleValueMap.get(order.sale_id) || 0;
        if (dealValue > 0) return sum;
        return sum + (order.total_cameras || 0);
      }, 0) || 0;

      const camerasDeployed = camerasCommercial + camerasTrial;

      // Fetch shipments for commercial orders to determine deployed vs on-order
      const commercialOrderIds = commercialOrders.map(o => o.id);
      let commercialDeployed = 0;
      
      if (commercialOrderIds.length > 0) {
        const { data: shipments, error: shipmentsError } = await supabase
          .from('order_shipments')
          .select('order_id, allocated_device_ids, status')
          .in('order_id', commercialOrderIds)
          .in('status', ['shipped', 'delivered', 'confirmed']);
        
        if (shipmentsError) {
          console.error("Error fetching shipments:", shipmentsError);
        } else {
          // Count devices from confirmed/delivered shipments
          const shipmentOrderIds = new Set(
            shipments?.map(s => s.order_id) || []
          );
          commercialDeployed = shipments?.reduce((sum, s) => {
            return sum + (s.allocated_device_ids?.length || 0);
          }, 0) || 0;

          // Fallback: orders delivered without shipment records (pre-shipment-system)
          for (const order of commercialOrders) {
            if (!shipmentOrderIds.has(order.id) && Array.isArray(order.allocated_device_ids)) {
              commercialDeployed += order.allocated_device_ids.length;
            }
          }
        }
      }

      const commercialOnOrder = camerasCommercial - commercialDeployed;
      
      // Create a set of sale IDs from paid deals (value > 0)
      const paidSaleIds = new Set(
        salesWithDeals
          ?.filter(sale => {
            const dealValue = parseFloat(sale.crm_deals?.value?.toString() || '0');
            return dealValue > 0;
          })
          .map(sale => sale.id) || []
      );
      
      // Only count images from paid orders
      const imagesPurchased = orders?.reduce((sum, order) => {
        if (!order.sale_id || !paidSaleIds.has(order.sale_id)) return sum;
        return sum + (order.total_images || 0);
      }, 0) || 0;
      
      // Calculate YTD revenue from paid sales
      const currentYear = new Date().getFullYear();
      const revenueYTD = salesData
        .filter(sale => {
          if (sale.status !== 'paid' && sale.status !== 'completed') return false;
          const saleYear = new Date(sale.created_at).getFullYear();
          return saleYear === currentYear;
        })
        .reduce((sum, sale) => sum + (parseFloat(sale.total_amount) || 0), 0);

      // Calculate ARR from delivered orders (annualized image revenue)
      const arr = salesWithDeals?.reduce((sum, sale) => {
        const deal = sale.crm_deals;
        if (!deal) return sum;
        return sum + getAnnualArrFromDeal(deal);
      }, 0) || 0;

      console.log("✅ Actuals KPI metrics calculated:", {
        commercialDeployed,
        commercialOnOrder,
        camerasTrial,
        camerasDeployed,
        revenueYTD,
        arr,
        imagesPurchased
      });

      return {
        commercialDeployed,
        commercialOnOrder,
        camerasTrial,
        camerasDeployed,
        revenueYTD,
        arr,
        imagesPurchased,
        imagesConsumed: null // TBD - requires image usage tracking
      };
    },
    staleTime: 60000, // Cache for 1 minute
  });

  // Query for Pipeline (Quoted/On Order)
  const pipelineQuery = useQuery({
    queryKey: ['kpi-pipeline'],
    queryFn: async () => {
      console.log("📊 Fetching pipeline KPI metrics...");
      
      // Get active deals (not won/lost, not archived)
      const { data: deals, error: dealsError } = await supabase
        .from('crm_deals')
        .select(`
          id,
          total_cameras,
          total_images,
          value,
          total_revenue,
          image_revenue,
          deal_data,
          status,
          stage:crm_deal_stages(name)
        `)
        .eq('archived', false)
        .not('status', 'in', '(won,lost)');
      
      if (dealsError) {
        console.error("Error fetching pipeline deals:", dealsError);
        throw dealsError;
      }

      // Get orders that are not yet delivered/shipped (on order)
      const { data: orders, error: ordersError } = await supabase
        .from('orders')
        .select(`
          id,
          total_cameras,
          total_images,
          status,
          sale_id
        `)
        .not('status', 'in', '(delivered,shipped,cancelled)');
      
      if (ordersError) {
        console.error("Error fetching pipeline orders:", ordersError);
      }

      // Split deals by stage: Opportunity vs Quoted (Proposal and other stages)
      const opportunityDeals = deals?.filter(deal => deal.stage?.name === 'Opportunity') || [];
      const quotedDeals = deals?.filter(deal => deal.stage?.name !== 'Opportunity') || [];

      // --- Opportunity metrics ---
      const camerasOpportunity = opportunityDeals.reduce((sum, deal) => sum + (deal.total_cameras || 0), 0);

      const revenueOpportunity = opportunityDeals.reduce((sum, deal) => {
        const dealValue = parseFloat(deal.value?.toString() || '0');
        if (dealValue <= 0) return sum;
        const dealData = deal.deal_data as any;
        const cameraCost = parseFloat(dealData?.camera_cost?.toString() || '0');
        const creditsCost = parseFloat(dealData?.credits_cost?.toString() || '0');
        const totalQuoteValue = cameraCost + creditsCost;
        return sum + (totalQuoteValue > 0 ? totalQuoteValue : dealValue);
      }, 0);

      const arrOpportunity = opportunityDeals.reduce((sum, deal) => {
        return sum + getAnnualArrFromDeal(deal);
      }, 0);

      const imagesOpportunity = opportunityDeals.reduce((sum, deal) => {
        const dealValue = parseFloat(deal.value?.toString() || '0');
        if (dealValue <= 0) return sum;
        return sum + (deal.total_images || 0);
      }, 0);

      // --- Quoted metrics (Proposal + other non-Opportunity stages) ---
      const camerasQuoted = quotedDeals.reduce((sum, deal) => sum + (deal.total_cameras || 0), 0);

      // Calculate cameras on order
      const camerasOnOrder = orders?.reduce((sum, order) => sum + (order.total_cameras || 0), 0) || 0;

      const revenueQuoted = quotedDeals.reduce((sum, deal) => {
        const dealValue = parseFloat(deal.value?.toString() || '0');
        if (dealValue <= 0) return sum;
        const dealData = deal.deal_data as any;
        const cameraCost = parseFloat(dealData?.camera_cost?.toString() || '0');
        const creditsCost = parseFloat(dealData?.credits_cost?.toString() || '0');
        const totalQuoteValue = cameraCost + creditsCost;
        return sum + (totalQuoteValue > 0 ? totalQuoteValue : dealValue);
      }, 0);

      const arrQuoted = quotedDeals.reduce((sum, deal) => {
        return sum + getAnnualArrFromDeal(deal);
      }, 0);

      const imagesQuoted = quotedDeals.reduce((sum, deal) => {
        const dealValue = parseFloat(deal.value?.toString() || '0');
        if (dealValue <= 0) return sum;
        return sum + (deal.total_images || 0);
      }, 0);

      const imagesOrdered = orders?.reduce((sum, order) => sum + (order.total_images || 0), 0) || 0;
      const imagesPipeline = imagesOpportunity + imagesQuoted + imagesOrdered;

      console.log("✅ Pipeline KPI metrics calculated:", {
        camerasOpportunity,
        revenueOpportunity,
        arrOpportunity,
        imagesOpportunity,
        camerasQuoted,
        camerasOnOrder,
        revenueQuoted,
        arrQuoted,
        imagesPipeline
      });

      return {
        camerasOpportunity,
        revenueOpportunity,
        arrOpportunity,
        imagesOpportunity,
        camerasQuoted,
        camerasOnOrder,
        revenueQuoted,
        revenueOnOrder: null,
        arrQuoted,
        imagesPipeline
      };
    },
    staleTime: 60000, // Cache for 1 minute
  });

  return {
    actualsQuery,
    pipelineQuery
  };
};
